import React, { useState, Component } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, InfoIcon, CheckIcon, ShieldCheckIcon, DownloadIcon, Loader2Icon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { generateNoordDigitalEmailHTML, generateCustomerEmailHTML } from '../utils/emailTemplates';
import { generateScanPdf } from '../utils/generatePdf';
interface Answer {
  text: string;
  info?: string;
}
interface Question {
  id: number;
  question: string;
  info: string;
  answers: Answer[];
  multiSelect?: boolean;
  showOtherInput?: boolean;
  condition?: (answers: Record<number, string[]>) => boolean;
}
// Define question IDs for reference
const Q_ORG_TYPE = 0;
const Q_ORG_SIZE = 1;
const Q_WEBSITE_TYPE = 2;
const Q_CURRENT_WEBSITE = 3;
const Q_GOAL = 4;
const Q_PAGES = 5;
const Q_REGULATIONS = 6;
const Q_SEO = 7;
const Q_CONTENT = 8;
const Q_HOSTING = 9;
const Q_TIMELINE = 10;
const Q_PRIORITY = 11;
// Price mappings for PDF
const PAGE_PRICES: Record<string, number> = {
  Home: 0,
  Diensten: 350,
  'Over ons': 350,
  Contact: 350,
  'Team / medewerkers': 350,
  'Klantportaal / login': 1500,
  'Blog / kennisbank': 500,
  Vacatures: 500,
  Overig: 350
};
const REGULATION_PRICES: Record<string, number> = {
  'AFM / Wft-communicatie': 150,
  'Privacy & AVG': 50,
  'Disclaimer / risicovermelding': 50,
  Cookiebeleid: 50,
  'Geen specifieke eisen': 0,
  Overig: 100
};
const questions: Question[] = [{
  id: Q_ORG_TYPE,
  question: 'Wat voor organisatie bent u?',
  info: 'Verschillende financiële organisaties hebben uiteenlopende eisen op het gebied van uitstraling, content en wetgeving. Door dit goed in kaart te brengen, stemmen wij de website beter af op uw branche en doelgroep.',
  answers: [{
    text: 'Financieel adviseur',
    info: 'Persoonlijke dienstverlening gericht op financieel advies en klantrelaties'
  }, {
    text: 'Administratiekantoor',
    info: 'Professionele dienstverlening met nadruk op betrouwbaarheid en efficiency'
  }, {
    text: 'Accountantskantoor',
    info: 'Formele uitstraling met strikte naleving van beroepsregels en compliance'
  }, {
    text: 'Hypotheekadviseur',
    info: 'Consumentgerichte communicatie met duidelijke informatievoorziening'
  }, {
    text: 'Vermogensbeheerder',
    info: 'Premium uitstraling met focus op expertise en resultaten'
  }, {
    text: 'Consultancybureau (financieel / strategisch)',
    info: 'Zakelijke dienstverlening met nadruk op expertise en strategisch inzicht'
  }, {
    text: 'Anders',
    info: 'Andere financiële of zakelijke dienstverlening'
  }],
  showOtherInput: true
}, {
  id: Q_ORG_SIZE,
  question: 'Hoe groot is uw organisatie?',
  info: "De omvang van uw organisatie zegt veel over de benodigde structuur en schaalbaarheid van de website. Grotere organisaties vragen vaak om meer pagina's en extra aandacht voor betrouwbaarheid.",
  answers: [{
    text: 'Zelfstandig',
    info: 'Eenmanszaak of zelfstandige zonder personeel'
  }, {
    text: '2–5 medewerkers',
    info: 'Klein team met beperkte structuur'
  }, {
    text: '6–15 medewerkers',
    info: 'Middelgroot kantoor met meerdere specialisaties'
  }, {
    text: '15+ medewerkers',
    info: 'Grote organisatie met uitgebreide structuur en afdelingen'
  }]
}, {
  id: Q_WEBSITE_TYPE,
  question: 'Wat voor type website wilt u laten maken?',
  info: 'Het type website bepaalt de basis van de prijs. Een onepager is eenvoudiger dan een volledige website, terwijl een webshop extra techniek en onderhoud vereist.',
  answers: [{
    text: 'Onepager / landingspagina',
    info: 'Eenvoudige website op één pagina, perfect voor campagnes of startende ondernemers'
  }, {
    text: "Normale website (meerdere pagina's)",
    info: "Volledige website met meerdere pagina's, structuur en navigatie"
  }, {
    text: 'Webshop',
    info: 'Website met verkoopfunctionaliteit, producten en betalingen'
  }]
}, {
  id: Q_CURRENT_WEBSITE,
  question: 'Heeft u op dit moment een website?',
  info: 'De huidige staat van uw website bepaalt waar de meeste verbetering mogelijk is. Denk aan uitstraling, techniek, snelheid, beveiliging en conversie.',
  answers: [{
    text: 'Nee, dit wordt mijn eerste website',
    info: 'Volledig nieuwe opzet zonder bestaande content of structuur'
  }, {
    text: 'Ja, maar deze is verouderd',
    info: 'Bestaande website die technisch of visueel niet meer voldoet'
  }, {
    text: 'Ja, maar voldoet niet meer aan wetgeving of uitstraling',
    info: 'Website die niet meer compliant is of professioneel genoeg oogt'
  }, {
    text: 'Ja, maar levert te weinig aanvragen op',
    info: 'Website die niet converteert of onvoldoende leads genereert'
  }]
}, {
  id: Q_GOAL,
  question: 'Wat is het belangrijkste doel van de nieuwe website?',
  info: 'Een website werkt het beste wanneer deze één of meerdere duidelijke doelen heeft. Dit helpt bij het bepalen van design, structuur en inhoud.',
  answers: [{
    text: 'Professionele uitstraling & vertrouwen',
    info: 'Focus op geloofwaardigheid en eerste indruk'
  }, {
    text: 'Meer aanvragen / leads',
    info: 'Conversie-optimalisatie en duidelijke call-to-actions'
  }, {
    text: 'Informatie voor bestaande klanten',
    info: 'Klantportaal, downloads en self-service mogelijkheden'
  }, {
    text: 'Autoriteit uitstralen binnen mijn vakgebied',
    info: 'Thought leadership, kennisdeling en positionering'
  }, {
    text: 'Voldoen aan wet- en regelgeving',
    info: 'Compliance met AFM, Wft en andere brancheregels'
  }],
  multiSelect: true
}, {
  id: Q_PAGES,
  question: "Welke pagina's verwacht u minimaal nodig te hebben?",
  info: "Het aantal en type pagina's bepalen de omvang en complexiteit van de website. Financiële websites bevatten vaak extra pagina's om vertrouwen en duidelijkheid te bieden.",
  answers: [{
    text: 'Home',
    info: 'Startpagina met overzicht en eerste indruk'
  }, {
    text: 'Diensten',
    info: 'Uitgebreide beschrijving van uw aanbod'
  }, {
    text: 'Over ons',
    info: 'Bedrijfsinformatie, missie en visie'
  }, {
    text: 'Contact',
    info: 'Contactformulier en bereikbaarheid'
  }, {
    text: 'Team / medewerkers',
    info: "Overzicht van uw team met foto's en expertise"
  }, {
    text: 'Klantportaal / login',
    info: 'Beveiligde omgeving voor klanten'
  }, {
    text: 'Blog / kennisbank',
    info: 'Artikelen en kennisdeling voor SEO en autoriteit'
  }, {
    text: 'Vacatures',
    info: 'Wervingspagina voor nieuwe medewerkers'
  }, {
    text: 'Overig',
    info: "Andere pagina's die u nodig heeft"
  }],
  multiSelect: true,
  showOtherInput: true,
  condition: answers => answers[Q_WEBSITE_TYPE]?.includes("Normale website (meerdere pagina's)")
}, {
  id: Q_REGULATIONS,
  question: 'Moet de website aan specifieke regels voldoen?',
  info: 'In de financiële sector gelden vaak strengere richtlijnen voor online communicatie. Door hier vooraf rekening mee te houden, voorkomen we aanpassingen achteraf.',
  answers: [{
    text: 'AFM / Wft-communicatie',
    info: 'Naleving van AFM-richtlijnen en Wft-verplichtingen'
  }, {
    text: 'Privacy & AVG',
    info: 'GDPR-compliance en privacyverklaring'
  }, {
    text: 'Disclaimer / risicovermelding',
    info: 'Wettelijk verplichte disclaimers en risicomeldingen'
  }, {
    text: 'Cookiebeleid',
    info: 'Cookie-consent en tracking volgens wetgeving'
  }, {
    text: 'Geen specifieke eisen',
    info: 'Geen bijzondere compliance-vereisten'
  }, {
    text: 'Overig',
    info: 'Andere specifieke regelgeving of branche-eisen'
  }],
  multiSelect: true,
  showOtherInput: true
}, {
  id: Q_SEO,
  question: 'Hoe belangrijk is vindbaarheid in Google voor u?',
  info: 'Goede vindbaarheid ontstaat niet vanzelf. Wij realiseren betere zichtbaarheid door het slim inzetten van SEO, afgestemd op uw diensten en doelgroep.',
  answers: [{
    text: 'Zeer belangrijk',
    info: 'SEO is cruciaal voor acquisitie en groei'
  }, {
    text: 'Belangrijk',
    info: 'Goede vindbaarheid draagt bij aan nieuwe klanten'
  }, {
    text: 'Enigszins belangrijk',
    info: 'Basis SEO is voldoende, geen actieve focus'
  }, {
    text: 'Niet belangrijk',
    info: 'Klanten komen via andere kanalen (netwerk, verwijzingen)'
  }]
}, {
  id: Q_CONTENT,
  question: 'Wilt u dat teksten door een specialist worden geschreven?',
  info: 'Heldere en correcte teksten zijn essentieel binnen de financiële sector. Professioneel geschreven content vergroot vertrouwen en conversie.',
  answers: [{
    text: 'Volledig laten schrijven (inhoudelijk correct, juridisch zorgvuldig en passend binnen financiële communicatie)',
    info: 'Wij schrijven alle content professioneel en compliant'
  }, {
    text: 'Samen herschrijven en optimaliseren',
    info: 'Wij verbeteren uw teksten voor leesbaarheid en SEO'
  }, {
    text: 'Nee, wij leveren alle teksten zelf aan',
    info: 'U schrijft alle content zelf en levert deze aan'
  }]
}, {
  id: Q_HOSTING,
  question: 'Hoe wilt u dat hosting, onderhoud en beveiliging worden geregeld?',
  info: 'Een betrouwbare en veilige website is essentieel in de financiële sector. Onze hosting omvat standaard onderhoud, beveiliging, back-ups, monitoring én persoonlijke support — zodat u zich volledig kunt richten op uw klanten.',
  answers: [{
    text: 'Volledig ontzorgd via Noord Digital (aanbevolen)',
    info: 'Inclusief hosting, SSL, dagelijkse back-ups, malware-scanning, 24/7 monitoring, updates en persoonlijke support. U hoeft nergens naar om te kijken.'
  }, {
    text: 'Externe hosting met technische ondersteuning',
    info: 'U regelt zelf hosting bij een externe partij. Wij moeten de website hierop afstemmen, wat extra configuratie en afstemming vereist (+10% projectkosten).'
  }, {
    text: 'Alles zelf regelen',
    info: 'U bent volledig zelf verantwoordelijk voor hosting, beveiliging, updates en back-ups. Extra configuratiewerk is nodig om de website overdraagbaar te maken (+10% projectkosten).'
  }]
}, {
  id: Q_TIMELINE,
  question: 'Wanneer wilt u de website live hebben?',
  info: 'De gewenste planning bepaalt de projectintensiteit en prioriteit. Kortere deadlines vragen om een strakkere samenwerking.',
  answers: [{
    text: 'Geen haast',
    info: 'Flexibele planning, kwaliteit boven snelheid'
  }, {
    text: 'Binnen 2–3 maanden',
    info: 'Normale doorlooptijd met ruimte voor iteraties'
  }, {
    text: 'Binnen 4–6 weken',
    info: 'Versnelde planning met intensieve samenwerking'
  }, {
    text: 'Zo snel mogelijk',
    info: 'Spoedproject met hoge prioriteit'
  }]
}, {
  id: Q_PRIORITY,
  question: 'Wat vindt u belangrijker bij de keuze voor een website?',
  info: 'Een website is een langetermijninvestering. Door uw prioriteit te bepalen, kunnen wij een passende prijsindicatie geven.',
  answers: [{
    text: 'Maximale kwaliteit, zekerheid en ontzorging',
    info: 'Kwaliteit en service zijn belangrijker dan prijs'
  }, {
    text: 'Goede balans tussen prijs en kwaliteit',
    info: 'Optimale prijs-kwaliteitverhouding'
  }, {
    text: 'Zo laag mogelijke prijs',
    info: 'Budget is de belangrijkste factor'
  }]
}];
export const PricingScan: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [answers, setAnswers] = useState<Record<number, string[]>>({});
  const [otherInputs, setOtherInputs] = useState<Record<number, string>>({});
  const [showInfo, setShowInfo] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [hoveredAnswer, setHoveredAnswer] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [formData, setFormData] = useState({
    company: '',
    email: '',
    name: '',
    phone: ''
  });
  const [consent, setConsent] = useState({
    privacy: false,
    marketing: false
  });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [expandedFeature, setExpandedFeature] = useState<number | null>(null);
  const currentQuestion = currentStep >= 0 && currentStep < questions.length ? questions[currentStep] : null;
  const isMultiSelect = currentQuestion?.multiSelect || false;
  const currentAnswers = currentStep >= 0 ? answers[currentStep] || [] : [];
  const hasOtherSelected = currentAnswers.includes('Overig') || currentAnswers.includes('Anders');
  const handleAnswerSelect = (answer: string) => {
    if (isMultiSelect) {
      const newAnswers = currentAnswers.includes(answer) ? currentAnswers.filter(a => a !== answer) : [...currentAnswers, answer];
      setAnswers({
        ...answers,
        [currentStep]: newAnswers
      });
    } else {
      setAnswers({
        ...answers,
        [currentStep]: [answer]
      });
    }
  };
  const canProceed = currentStep === -1 ? formData.company.trim() !== '' : currentAnswers.length > 0;
  const getNextStepIndex = (currentIndex: number) => {
    let nextIndex = currentIndex + 1;
    while (nextIndex < questions.length) {
      const question = questions[nextIndex];
      if (!question.condition || question.condition(answers)) {
        return nextIndex;
      }
      nextIndex++;
    }
    return -1;
  };
  const getPrevStepIndex = (currentIndex: number) => {
    let prevIndex = currentIndex - 1;
    while (prevIndex >= 0) {
      const question = questions[prevIndex];
      if (!question.condition || question.condition(answers)) {
        return prevIndex;
      }
      prevIndex--;
    }
    return -1;
  };
  const handleNext = () => {
    const nextIndex = getNextStepIndex(currentStep);
    if (nextIndex !== -1) {
      setCurrentStep(nextIndex);
      setShowInfo(false);
      setHoveredAnswer(null);
    } else {
      setShowResults(true);
    }
  };
  const handlePrevious = () => {
    if (showResults) {
      setShowResults(false);
      return;
    }
    if (currentStep === -1) return;
    const prevIndex = getPrevStepIndex(currentStep);
    setCurrentStep(prevIndex);
    setShowInfo(false);
    setHoveredAnswer(null);
  };
  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (consent.privacy) {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const pricing = calculatePricing();
        const includedFeatures = getIncludedFeatures();
        // Prepare data
        const allAnswers = questions.filter(q => !q.condition || q.condition(answers)).map(q => {
          const ans = answers[q.id];
          const otherText = otherInputs[q.id] ? ` (${otherInputs[q.id]})` : '';
          return {
            question: q.question,
            answer: `${ans ? ans.join(', ') : 'Niet beantwoord'}${otherText}`
          };
        });
        const emailData = {
          formData,
          pricing,
          includedFeatures,
          allAnswers,
          marketingConsent: consent.marketing
        };
        // Generate HTML templates
        const noordDigitalHtml = generateNoordDigitalEmailHTML(emailData);
        const adminHtml = noordDigitalHtml;
        // Base64-encode HTML to bypass WAF
        const response = await fetch('https://noorddigital.nl/prijsscan-api.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            to: formData.email,
            companyName: formData.company,
            adminHtml: btoa(unescape(encodeURIComponent(adminHtml))),
            customerHtml: btoa(unescape(encodeURIComponent(generateCustomerEmailHTML(emailData)))),
            subjectAdmin: `Prijsscan aanvraag – ${formData.company}`,
            subjectCustomer: `Uw persoonlijke website plan – ${formData.company}`,
            encoded: true
          })
        });
        const result = await response.json();
        if (!result.success) {
          throw new Error(result.message || 'Verzending mislukt');
        }
        setContactSubmitted(true);
      } catch (error) {
        console.error('Submission error:', error);
        setSubmitError('Er is een fout opgetreden bij het versturen. Probeer het later opnieuw of neem contact op via info@noorddigital.nl');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  const handleDownloadPdf = () => {
    const pricing = calculatePricing();
    const includedFeatures = getIncludedFeatures();
    const allAnswers = questions.filter(q => !q.condition || q.condition(answers)).map(q => {
      const ans = answers[q.id];
      const otherText = otherInputs[q.id] ? ` (${otherInputs[q.id]})` : '';
      return {
        question: q.question,
        answer: `${ans ? ans.join(', ') : 'Niet beantwoord'}${otherText}`
      };
    });
    generateScanPdf({
      formData,
      pricing,
      includedFeatures,
      allAnswers
    });
  };
  const calculatePricing = () => {
    let oneTime = 0;
    let monthlyBase = 0;
    const yearly = 149;
    const websiteType = answers[Q_WEBSITE_TYPE]?.[0];
    const orgSize = answers[Q_ORG_SIZE]?.[0];
    const pages = answers[Q_PAGES] || [];
    const hosting = answers[Q_HOSTING]?.[0];
    const seo = answers[Q_SEO]?.[0];
    const content = answers[Q_CONTENT]?.[0];
    const regulations = answers[Q_REGULATIONS] || [];
    // 1. Base price by website type
    if (websiteType?.includes('Onepager')) {
      oneTime = 750;
    } else if (websiteType?.includes('Webshop')) {
      oneTime = 3000;
    } else {
      // Normale website
      oneTime = 1200;
    }
    // 2. Add page costs (only for 'Normale website')
    if (websiteType?.includes('Normale website')) {
      pages.forEach(page => {
        const price = PAGE_PRICES[page] || 350;
        oneTime += price;
      });
    }
    // 3. Add regulation costs
    if (!regulations.includes('Geen specifieke eisen')) {
      regulations.forEach(reg => {
        const price = REGULATION_PRICES[reg] || 0;
        oneTime += price;
      });
    }
    // 4. Add SEO setup cost
    if (seo?.includes('Zeer belangrijk')) {
      oneTime += 1000;
    } else if (seo?.includes('Belangrijk')) {
      oneTime += 500;
    } else if (seo?.includes('Enigszins belangrijk')) {
      oneTime += 250;
    }
    // 5. Add content writing cost
    if (content?.includes('Volledig laten schrijven')) {
      // €150 per page excluding Home
      const pagesExcludingHome = pages.filter(p => p !== 'Home');
      oneTime += 150 * pagesExcludingHome.length;
    } else if (content?.includes('Samen herschrijven')) {
      oneTime += 50;
    }
    // 6. Apply org size multiplier
    let multiplier = 1.0;
    if (orgSize?.includes('Zelfstandig')) {
      multiplier = 0.75;
    } else if (orgSize?.includes('2–5')) {
      multiplier = 1.0;
    } else if (orgSize?.includes('6–15')) {
      multiplier = 1.25;
    } else if (orgSize?.includes('15+')) {
      multiplier = 1.5;
    }
    oneTime = Math.round(oneTime * multiplier);
    // 7. Add 10% surcharge for external hosting (extra configuration work)
    if (hosting && !hosting.includes('Volledig ontzorgd')) {
      oneTime = Math.round(oneTime * 1.1);
    }
    // Monthly costs - capped at €60
    if (hosting?.includes('Volledig ontzorgd')) {
      const pagesExcludingHome = pages.filter(p => p !== 'Home');
      monthlyBase = 20 + 5 * pagesExcludingHome.length;
      // Cap at €60
      monthlyBase = Math.min(monthlyBase, 60);
    }
    // Return as range: oneTime ± 500
    const oneTimeMin = Math.max(0, oneTime - 500);
    const oneTimeMax = oneTime + 500;
    return {
      oneTime: {
        min: oneTimeMin,
        max: oneTimeMax
      },
      monthly: monthlyBase,
      yearly
    };
  };
  const getIncludedFeatures = () => {
    const features: Array<{
      text: string;
      tooltip: string;
    }> = [];
    const websiteType = answers[Q_WEBSITE_TYPE]?.[0];
    const pages = answers[Q_PAGES] || [];
    const hosting = answers[Q_HOSTING]?.[0];
    const seo = answers[Q_SEO]?.[0];
    const content = answers[Q_CONTENT]?.[0];
    // TOP PRIORITY ITEMS - Always first
    features.push({
      text: 'Persoonlijke begeleiding & advies',
      tooltip: 'Directe toegang tot je vaste contactpersoon voor strategisch advies, sparren over keuzes en begeleiding door het hele proces.'
    });
    features.push({
      text: '100% tevredenheidsgarantie',
      tooltip: 'Onbeperkte revisies totdat u volledig tevreden bent, geld-terug-garantie als wij niet aan uw verwachtingen voldoen, en persoonlijke begeleiding gedurende het hele proces.'
    });
    // CORE WEBSITE FEATURES
    if (websiteType?.includes('Onepager')) {
      features.push({
        text: 'Professionele Onepager / Landingspagina',
        tooltip: 'Eén krachtige pagina die je boodschap helder overbrengt en bezoekers converteert naar klanten.'
      });
    } else if (websiteType?.includes('Webshop')) {
      features.push({
        text: 'Complete Webshop functionaliteit',
        tooltip: 'Volledig werkende webshop met productbeheer, voorraadadministratie en betalingsintegratie.'
      });
      features.push({
        text: 'Productbeheer & Categorieën',
        tooltip: 'Eenvoudig producten toevoegen, bewerken en organiseren in overzichtelijke categorieën.'
      });
      features.push({
        text: 'Winkelwagen & Checkout',
        tooltip: 'Geoptimaliseerd bestelproces met veilige betalingsmogelijkheden en orderbevestigingen.'
      });
    } else {
      features.push({
        text: `Website met ± ${Math.max(pages.length, 4)} pagina's`,
        tooltip: `Professionele meerpagins website met ${Math.max(pages.length, 4)} pagina's, inclusief navigatie en logische structuur.`
      });
    }
    // HOSTING & TECHNICAL
    if (hosting?.includes('Volledig ontzorgd')) {
      features.push({
        text: 'Hosting & onderhoud',
        tooltip: 'Betrouwbare hosting op snelle servers met dagelijkse back-ups en proactief onderhoud.'
      });
      features.push({
        text: 'Beveiliging & dagelijkse back-ups',
        tooltip: 'SSL-certificaat, firewall, malware-scanning en automatische dagelijkse back-ups voor maximale veiligheid.'
      });
      features.push({
        text: 'Monitoring & uptime garantie',
        tooltip: '24/7 monitoring van je website met directe alerts bij problemen en 99.9% uptime garantie.'
      });
    }
    // SEO CONFIGURATION - Check for Premium (second-best and best)
    const isVeryHighSEO = seo?.includes('Zeer belangrijk');
    const isHighSEO = seo?.includes('Belangrijk');
    if (isVeryHighSEO || isHighSEO) {
      features.push({
        text: 'Yoast Premium-configuratie',
        tooltip: 'Geavanceerde SEO-instellingen met Yoast Premium voor optimale vindbaarheid in zoekmachines.'
      });
    } else {
      features.push({
        text: 'Basis Yoast SEO-configuratie',
        tooltip: 'Standaard SEO-optimalisatie met Yoast SEO voor goede vindbaarheid in Google.'
      });
    }
    features.push({
      text: 'Responsive design (mobiel & desktop)',
      tooltip: 'Uw website ziet er perfect uit op alle apparaten: smartphone, tablet en desktop.'
    });
    features.push({
      text: 'AVG-compliant privacy instellingen',
      tooltip: 'Volledige naleving van AVG-wetgeving met cookiemelding, privacyverklaring en gegevensbescherming.'
    });
    // SPECIAL FEATURES
    if (pages.some(p => p.includes('Klantportaal'))) {
      features.push({
        text: 'Beveiligd klantportaal',
        tooltip: 'Afgeschermde omgeving waar klanten kunnen inloggen voor documenten, gegevens of persoonlijke informatie.'
      });
    }
    if (pages.some(p => p.includes('Blog')) || websiteType?.includes('Webshop')) {
      features.push({
        text: 'Blog/kennisbank functionaliteit',
        tooltip: 'Publiceer eenvoudig artikelen, nieuws en kennisitems om autoriteit op te bouwen en SEO te verbeteren.'
      });
    }
    if (isVeryHighSEO || isHighSEO) {
      features.push({
        text: 'Uitgebreide SEO-optimalisatie',
        tooltip: 'Diepgaande zoekwoordanalyse, technische SEO, snelheidsoptimalisatie en gestructureerde data voor maximale vindbaarheid.'
      });
    }
    if (content?.includes('Volledig laten schrijven')) {
      features.push({
        text: 'Professioneel geschreven content',
        tooltip: 'Alle teksten worden door ervaren copywriters geschreven, afgestemd op jouw doelgroep en geoptimaliseerd voor conversie en SEO.'
      });
    }
    features.push({
      text: 'Onbeperkte revisies tijdens ontwikkeling',
      tooltip: 'Zoveel aanpassingen als nodig tijdens het ontwikkelproces tot je volledig tevreden bent met het resultaat.'
    });
    // TRAINING - Separate item at the end
    features.push({
      text: 'Training',
      tooltip: 'Persoonlijke training in het gebruik van je website, zodat je zelfstandig content kunt beheren en aanpassen.'
    });
    return features;
  };
  // FIRST: Check if showing results
  if (showResults) {
    const pricing = calculatePricing();
    const includedFeatures = getIncludedFeatures();
    const hosting = answers[Q_HOSTING]?.[0];
    const seo = answers[Q_SEO]?.[0];
    const isHighSEO = seo?.includes('Zeer belangrijk');
    return <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex items-center justify-center p-4 py-12" data-id="element-6">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6
      }} className="w-full max-w-5xl" data-id="element-7">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-8" data-id="element-8">
            <div className="bg-gradient-to-br from-[#007AFF] to-[#005FCC] p-8 md:p-12 text-white" data-id="element-9">
              <div className="max-w-3xl mx-auto text-center" data-id="element-10">
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-3 tracking-tight" data-id="element-11">
                  Persoonlijk plan voor {formData.company}
                </h1>
                <p className="text-blue-100 text-xl mb-8 leading-relaxed" data-id="element-12">
                  Op basis van uw antwoorden
                </p>

                <div className="border-t border-blue-300 opacity-30 my-6" data-id="element-13"></div>

                <p className="text-blue-100 text-sm font-bold mb-3 uppercase tracking-wider" data-id="element-14">
                  Geschatte investering
                </p>
                <div className="text-5xl md:text-6xl font-bold mb-4" data-id="element-15">
                  €{pricing.oneTime.min.toLocaleString()} – €
                  {pricing.oneTime.max.toLocaleString()}
                </div>
                <p className="text-blue-50 text-lg" data-id="element-16">
                  Websitebouw, design, maatwerk en volledige implementatie
                </p>
              </div>
            </div>

            <div className="p-8 md:p-12" data-id="element-17">
              <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-18">
                {pricing.monthly > 0 && <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 border-2 border-blue-100" data-id="element-19">
                    <p className="text-gray-700 text-sm font-bold mb-3 uppercase tracking-wide" data-id="element-20">
                      Maandelijkse kosten
                    </p>
                    <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-21">
                      €{pricing.monthly}{' '}
                      <span className="text-lg font-normal text-gray-500" data-id="element-22">
                        p/m
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed" data-id="element-23">
                      Hosting, onderhoud, beveiliging, back-ups en monitoring
                    </p>
                  </div>}

                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-200" data-id="element-24">
                  <p className="text-gray-700 text-sm font-bold mb-3 uppercase tracking-wide" data-id="element-25">
                    Jaarlijkse kosten
                  </p>
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-26">
                    €{pricing.yearly}{' '}
                    <span className="text-lg font-normal text-gray-500" data-id="element-27">
                      p/j
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-28">
                    Yoast SEO Premium licentie
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-xl p-5 mb-10" data-id="element-29">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-30">
                  <div className="flex items-start" data-id="element-31">
                    <InfoIcon className="h-5 w-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" data-id="element-32" />
                    <div data-id="element-33">
                      <p className="text-gray-900 font-semibold mb-1" data-id="element-34">
                        Dit is een indicatie
                      </p>
                      <p className="text-gray-700 text-sm leading-relaxed" data-id="element-35">
                        Deze prijsindicatie is gebaseerd op uw antwoorden en
                        geeft een globale schatting. Neem vrijblijvend contact
                        met ons op voor een exacte prijs op maat.
                      </p>
                    </div>
                  </div>
                  <button onClick={() => {
                  document.getElementById('contact-section')?.scrollIntoView({
                    behavior: 'smooth'
                  });
                }} className="flex-shrink-0 inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-colors shadow-sm hover:shadow-md" data-id="element-36">
                    Vraag een exacte prijs aan
                    <ChevronRightIcon className="h-4 w-4 ml-1.5" data-id="element-37" />
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 mb-12" data-id="element-38">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-39">
                  Wat is inbegrepen?
                </h3>
                <div className="grid md:grid-cols-2 gap-4" data-id="element-40">
                  {includedFeatures.map((feature, index) => <div key={index} className="relative" data-id="element-41">
                      <div className="flex items-start bg-white rounded-lg p-3 shadow-sm" data-id="element-42">
                        <div className="flex-shrink-0 w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center mr-3 mt-0.5" data-id="element-43">
                          <CheckIcon className="h-4 w-4 text-white" data-id="element-44" />
                        </div>
                        <span className="text-gray-700 text-sm font-medium leading-relaxed flex-1" data-id="element-45">
                          {feature.text}
                        </span>
                        <button onClick={() => setExpandedFeature(expandedFeature === index ? null : index)} className="flex-shrink-0 ml-2 p-1 hover:bg-gray-100 rounded-full transition-colors" aria-label="Meer informatie" data-id="element-46">
                          <InfoIcon className="h-4 w-4 text-gray-400 hover:text-[#007AFF]" data-id="element-47" />
                        </button>
                      </div>
                      <AnimatePresence data-id="element-48">
                        {expandedFeature === index && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-49">
                            <div className="mt-2 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl" data-id="element-50">
                              {feature.tooltip}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>)}
                </div>
              </div>

              <div className="border-t-2 border-gray-100 pt-10" id="contact-section" data-id="element-51">
                {!contactSubmitted ? <div className="max-w-2xl mx-auto" data-id="element-52">
                    <div className="text-center mb-8" data-id="element-53">
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3" data-id="element-54">
                        Ontvang uw persoonlijke offerte
                      </h3>
                      <p className="text-gray-600 text-lg mb-4" data-id="element-55">
                        Laat uw gegevens achter en we nemen vrijblijvend contact
                        met u op om deze indicatie toe te lichten.
                      </p>
                      <div className="bg-blue-50 border-l-4 border-[#007AFF] rounded-r-xl p-4 text-left" data-id="element-56">
                        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-57">
                          <strong className="text-[#007AFF]" data-id="element-58">
                            Geen verkooppraatjes:
                          </strong>{' '}
                          Noord Digital neemt éénmalig contact met u op om een
                          vrijblijvende offerte te versturen. U wordt niet
                          ingeschreven voor een nieuwsbrief en ontvangt geen
                          verkoopgesprekken.
                        </p>
                      </div>
                    </div>

                    <form onSubmit={handleContactSubmit} className="space-y-6" data-id="element-59">
                      <div className="grid md:grid-cols-2 gap-4" data-id="element-60">
                        <div data-id="element-61">
                          <label className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-62">
                            Naam
                          </label>
                          <input type="text" required value={formData.name} onChange={e => setFormData({
                        ...formData,
                        name: e.target.value
                      })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF] outline-none transition text-gray-900" placeholder="Uw naam" data-id="element-63" />
                        </div>
                        <div data-id="element-64">
                          <label className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-65">
                            E-mailadres <span className="text-red-500" data-id="element-66">*</span>
                          </label>
                          <input type="email" required value={formData.email} onChange={e => setFormData({
                        ...formData,
                        email: e.target.value
                      })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF] outline-none transition text-gray-900" placeholder="uw@email.nl" data-id="element-67" />
                        </div>
                      </div>

                      <div data-id="element-68">
                        <label className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-69">
                          Telefoonnummer{' '}
                          <span className="text-gray-400 font-normal" data-id="element-70">
                            (optioneel)
                          </span>
                        </label>
                        <input type="tel" value={formData.phone} onChange={e => setFormData({
                      ...formData,
                      phone: e.target.value
                    })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF] outline-none transition text-gray-900" placeholder="06 12345678" data-id="element-71" />
                      </div>

                      <div className="bg-blue-50 rounded-xl p-4" data-id="element-72">
                        <label className="flex items-start cursor-pointer" data-id="element-73">
                          <input type="checkbox" checked={consent.privacy} onChange={e => setConsent({
                        ...consent,
                        privacy: e.target.checked
                      })} className="mt-1 h-5 w-5 text-[#007AFF] border-2 border-gray-300 rounded focus:ring-2 focus:ring-[#007AFF] cursor-pointer" required data-id="element-74" />
                          <span className="ml-3 text-sm text-gray-800 leading-relaxed" data-id="element-75">
                            <strong className="text-red-500" data-id="element-76">*</strong> Ik ga
                            akkoord met het verwerken van mijn gegevens zoals
                            beschreven in de{' '}
                            <a href="https://noorddigital.nl/algemene-voorwaarden/" target="_blank" rel="noopener noreferrer" className="text-[#007AFF] hover:underline font-semibold" data-id="element-77">
                              algemene voorwaarden
                            </a>
                            .
                          </span>
                        </label>
                      </div>

                      <div className="bg-green-50 border border-green-200 rounded-xl p-4" data-id="element-78">
                        <p className="text-sm text-green-800 leading-relaxed" data-id="element-79">
                          <strong data-id="element-80">Na het versturen</strong> ontvangt u direct
                          een bevestiging per e-mail én kunt u uw persoonlijke
                          plan als PDF downloaden.
                        </p>
                      </div>

                      <motion.button whileHover={{
                    scale: 1.02
                  }} whileTap={{
                    scale: 0.98
                  }} type="submit" disabled={!consent.privacy || !formData.name || isSubmitting} className="w-full flex items-center justify-center px-6 py-4 rounded-xl bg-gradient-to-r from-[#007AFF] to-[#005FCC] hover:from-[#005FCC] hover:to-[#004BA8] text-white font-bold transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl" data-id="element-81">
                        {isSubmitting ? <>
                            <Loader2Icon className="h-5 w-5 mr-2 animate-spin" data-id="element-82" />
                            Versturen...
                          </> : <>
                            Verstuur aanvraag
                            <ChevronRightIcon className="h-5 w-5 ml-2" data-id="element-83" />
                          </>}
                      </motion.button>

                      {submitError && <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm text-center" data-id="element-84">
                          {submitError}
                        </div>}
                    </form>
                  </div> : <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 border-2 border-green-100 text-center max-w-2xl mx-auto" data-id="element-85">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4" data-id="element-86">
                      <CheckIcon className="h-8 w-8 text-green-600" data-id="element-87" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-88">
                      Bedankt voor uw aanvraag!
                    </h3>
                    <p className="text-gray-600 mb-6" data-id="element-89">
                      We hebben uw gegevens ontvangen en een bevestiging per
                      e-mail gestuurd.
                    </p>

                    <button onClick={handleDownloadPdf} className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[#007AFF] text-white font-semibold hover:bg-[#005FCC] transition shadow-md" data-id="element-90">
                      <DownloadIcon className="h-5 w-5 mr-2" data-id="element-91" />
                      Download Resultaat als PDF
                    </button>
                  </motion.div>}
              </div>
            </div>
          </div>
        </motion.div>
      </div>;
  }
  // SECOND: Check if on company info step
  if (currentStep === -1) {
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && canProceed) {
        e.preventDefault();
        handleNext();
      }
    };
    return <div className="min-h-screen bg-gradient-to-br from-[#F7F8FA] to-white flex items-center justify-center p-4" data-id="element-92">
        <div className="w-full max-w-2xl relative" data-id="element-93">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.5
        }} className="hidden lg:block absolute -left-40 top-20 w-32" data-id="element-94">
            <div className="bg-white border-2 border-[#007AFF] text-[#007AFF] p-3 rounded-xl rounded-br-none shadow-sm text-sm font-bold mb-2 text-center" data-id="element-95">
              Ik help je met de scan!
            </div>
            <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte - kosten professionele website accountantskantoor" className="w-24 h-24 object-contain rounded-full border-4 border-white shadow-md mx-auto" data-id="element-96" />
          </motion.div>

          <div className="mb-8" data-id="element-97">
            <div className="flex items-center justify-between mb-2" data-id="element-98">
              <span className="text-sm font-semibold text-[#111827]" data-id="element-99">
                Stap 1 van {questions.length + 1}
              </span>
              <span className="text-sm text-[#6B7280]" data-id="element-100">
                {Math.round(1 / (questions.length + 1) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden" data-id="element-101">
              <motion.div className="bg-[#007AFF] h-full" initial={{
              width: 0
            }} animate={{
              width: `${1 / (questions.length + 1) * 100}%`
            }} transition={{
              duration: 0.5
            }} data-id="element-102" />
            </div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white rounded-2xl shadow-2xl p-8 md:p-12" onKeyDown={handleKeyDown} data-id="element-103">
            <div className="text-center mb-8" data-id="element-104">
              <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-4" data-id="element-105">
                Welkom bij de Website Scan
              </h2>
              <p className="text-lg text-[#6B7280] leading-relaxed" data-id="element-106">
                Vul eerst uw bedrijfsgegevens in om te starten met de scan
              </p>
            </div>

            <form onSubmit={e => e.preventDefault()} className="space-y-6" data-id="element-107">
              <div data-id="element-108">
                <label className="block text-sm font-bold text-[#111827] mb-2" data-id="element-109">
                  Bedrijfsnaam <span className="text-red-500" data-id="element-110">*</span>
                </label>
                <input type="text" required value={formData.company} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} onKeyDown={handleKeyDown} className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF] outline-none transition text-gray-900" placeholder="Naam van uw organisatie" data-id="element-111" />
              </div>

              <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} type="button" onClick={handleNext} disabled={!canProceed} className="w-full flex items-center justify-center px-6 py-4 rounded-xl bg-[#007AFF] hover:bg-[#005FCC] text-white font-bold transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl" data-id="element-112">
                Start de scan
                <ChevronRightIcon className="h-5 w-5 ml-2" data-id="element-113" />
              </motion.button>

              <p className="text-xs text-[#6B7280] text-center leading-relaxed" data-id="element-114">
                Wij behandelen uw gegevens vertrouwelijk conform onze{' '}
                <a href="https://noorddigital.nl/algemene-voorwaarden/" target="_blank" rel="noopener noreferrer" className="text-[#007AFF] hover:underline" data-id="element-115">
                  algemene voorwaarden
                </a>
              </p>
            </form>
          </motion.div>
        </div>
      </div>;
  }
  // THIRD: Questions flow (default)
  const handleQuestionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canProceed) {
      e.preventDefault();
      handleNext();
    }
  };
  // Calculate tooltip position with viewport clamping
  const getTooltipPosition = () => {
    const tooltipWidth = 256; // max-w-xs = 256px
    const tooltipHeight = 80; // approximate height
    const padding = 20;
    let left = mousePosition.x + padding;
    let top = mousePosition.y - padding;
    // Clamp to right edge
    if (left + tooltipWidth > window.innerWidth - padding) {
      left = mousePosition.x - tooltipWidth - padding;
    }
    // Clamp to left edge
    if (left < padding) {
      left = padding;
    }
    // Clamp to bottom edge
    if (top + tooltipHeight > window.innerHeight - padding) {
      top = window.innerHeight - tooltipHeight - padding;
    }
    // Clamp to top edge
    if (top < padding) {
      top = padding;
    }
    return {
      left,
      top
    };
  };
  return <div className="min-h-screen bg-gradient-to-br from-[#F7F8FA] to-white flex items-center justify-center p-4" onKeyDown={handleQuestionKeyDown} tabIndex={0} data-id="element-116">
      <div className="w-full max-w-3xl relative" data-id="element-117">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.5
      }} className="hidden lg:block absolute -left-40 top-32" data-id="element-118">
          <motion.img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte - Website scan uitleg" className="w-24 h-24 object-contain rounded-full border-4 border-white shadow-md mx-auto" data-id="element-119" />

          <div className="text-center mt-2 text-xs text-[#6B7280] font-medium" data-id="element-120">
            Hover voor uitleg
          </div>
        </motion.div>

        <div className="mb-8" data-id="element-121">
          <div className="flex items-center justify-between mb-2" data-id="element-122">
            <span className="text-sm font-semibold text-[#111827]" data-id="element-123">
              Stap {currentStep + 2} van {questions.length + 1}
            </span>
            <span className="text-sm text-[#6B7280]" data-id="element-124">
              {Math.round((currentStep + 2) / (questions.length + 1) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden" data-id="element-125">
            <motion.div className="bg-[#007AFF] h-full" initial={{
            width: `${(currentStep + 1) / (questions.length + 1) * 100}%`
          }} animate={{
            width: `${(currentStep + 2) / (questions.length + 1) * 100}%`
          }} transition={{
            duration: 0.5
          }} data-id="element-126" />
          </div>
        </div>

        <motion.div key={currentStep} initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} transition={{
        duration: 0.3
      }} className="bg-white rounded-2xl shadow-2xl p-8 md:p-12" data-id="element-127">
          <div className="flex items-start justify-between mb-6" data-id="element-128">
            <h2 className="text-2xl md:text-3xl font-bold text-[#111827] pr-4" data-id="element-129">
              {currentQuestion?.question}
            </h2>
            <button onClick={() => setShowInfo(!showInfo)} className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-[#F7F8FA] hover:bg-[#007AFF] hover:text-white transition-colors" aria-label="Meer informatie" data-id="element-130">
              <InfoIcon className="h-5 w-5" data-id="element-131" />
            </button>
          </div>

          <AnimatePresence data-id="element-132">
            {showInfo && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="bg-[#E6F2FF] border-l-4 border-[#007AFF] rounded-lg p-4 mb-6 overflow-hidden" data-id="element-133">
                <p className="text-[#111827] text-sm leading-relaxed" data-id="element-134">
                  {currentQuestion?.info}
                </p>
              </motion.div>}
          </AnimatePresence>

          {isMultiSelect && <p className="text-sm text-[#6B7280] mb-4 italic" data-id="element-135">
              Meerdere antwoorden mogelijk
            </p>}

          <div className="space-y-3 mb-6" data-id="element-136">
            {currentQuestion?.answers.map((answer, index) => {
            const isSelected = currentAnswers.includes(answer.text);
            const isHovered = hoveredAnswer === answer.text;
            const isHostingQuestion = currentQuestion?.id === Q_HOSTING;
            const isRecommended = isHostingQuestion && index === 0;
            const isNotRecommended = isHostingQuestion && index > 0;
            return <div key={index} className="relative" data-id="element-137">
                  {isRecommended && <div className="absolute -top-3 left-4 z-10" data-id="element-138">
                      <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm" data-id="element-139">
                        ✓ Aanbevolen
                      </span>
                    </div>}
                  <motion.button whileHover={{
                scale: 1.01
              }} whileTap={{
                scale: 0.99
              }} onClick={() => handleAnswerSelect(answer.text)} onMouseEnter={() => setHoveredAnswer(answer.text)} onMouseLeave={() => setHoveredAnswer(null)} onMouseMove={e => {
                setMousePosition({
                  x: e.clientX,
                  y: e.clientY
                });
              }} className={`w-full text-left px-6 py-4 rounded-xl border-2 transition-all duration-200 ${isSelected ? isRecommended ? 'border-green-500 bg-green-50 shadow-md ring-2 ring-green-200' : 'border-[#007AFF] bg-[#E6F2FF] shadow-md' : isRecommended ? 'border-green-300 bg-green-50/50 hover:border-green-500 hover:bg-green-50' : isNotRecommended ? 'border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-50 opacity-80' : 'border-gray-200 hover:border-[#007AFF] hover:bg-[#F7F8FA]'} ${isRecommended ? 'pt-5' : ''}`} data-id="element-140">
                    <div className="flex items-center justify-between" data-id="element-141">
                      <div className="flex-1" data-id="element-142">
                        <span className={`font-medium ${isSelected ? isRecommended ? 'text-green-700' : 'text-[#007AFF]' : isRecommended ? 'text-green-800' : isNotRecommended ? 'text-gray-500' : 'text-[#111827]'}`} data-id="element-143">
                          {answer.text}
                        </span>
                        {isRecommended && !isSelected && <p className="text-green-600 text-xs mt-1 font-medium" data-id="element-144">
                            Inclusief SSL, back-ups, monitoring & support
                          </p>}
                        {isNotRecommended && !isSelected && <p className="text-amber-600 text-xs mt-1 font-medium" data-id="element-145">
                            +10% projectkosten vanwege extra configuratie
                          </p>}
                      </div>
                      {isSelected && <CheckIcon className={`h-5 w-5 flex-shrink-0 ${isRecommended ? 'text-green-600' : 'text-[#007AFF]'}`} data-id="element-146" />}
                    </div>
                  </motion.button>
                </div>;
          })}
          </div>

          <AnimatePresence data-id="element-147">
            {hoveredAnswer && currentQuestion?.answers.find(a => a.text === hoveredAnswer)?.info && <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} style={{
            position: 'fixed',
            left: getTooltipPosition().left,
            top: getTooltipPosition().top,
            pointerEvents: 'none',
            zIndex: 9999
          }} className="bg-[#111827] text-white text-sm rounded-lg p-3 shadow-xl max-w-xs" data-id="element-148">
                  <p className="leading-relaxed" data-id="element-149">
                    {currentQuestion?.answers.find(a => a.text === hoveredAnswer)?.info}
                  </p>
                </motion.div>}
          </AnimatePresence>

          {currentQuestion?.showOtherInput && hasOtherSelected && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} className="mb-6" data-id="element-150">
              <label className="block text-sm font-semibold text-[#111827] mb-2" data-id="element-151">
                Specificeer{' '}
                {currentStep === 0 ? 'uw organisatietype' : '(optioneel)'}
              </label>
              <input type="text" value={otherInputs[currentStep] || ''} onChange={e => setOtherInputs({
            ...otherInputs,
            [currentStep]: e.target.value
          })} onKeyDown={handleQuestionKeyDown} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007AFF] focus:border-transparent outline-none transition" placeholder={currentStep === 0 ? 'bijv. Belastingadviseur, Pensioenadviseur...' : 'Vul hier uw specificatie in...'} data-id="element-152" />
            </motion.div>}

          <div className="flex gap-4" data-id="element-153">
            <button onClick={handlePrevious} className="flex items-center justify-center px-6 py-3 rounded-lg border-2 border-gray-300 text-[#111827] font-semibold hover:bg-gray-50 transition" data-id="element-154">
              <ChevronLeftIcon className="h-5 w-5 mr-2" data-id="element-155" />
              Vorige
            </button>
            <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={handleNext} disabled={!canProceed} className="flex-1 flex items-center justify-center px-6 py-3 rounded-lg bg-[#007AFF] hover:bg-[#005FCC] text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg" data-id="element-156">
              {currentStep === questions.length - 1 ? 'Bekijk resultaat' : 'Volgende'}
              <ChevronRightIcon className="h-5 w-5 ml-2" data-id="element-157" />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>;
};